import type React from 'react';
import { Card, Heading, Paragraph } from '@zillow/constellation-10';
import { getBooleanConfig } from '@zg-rentals/frontend-config/client';
import PrimaryCTA from './PrimaryCTA';
import SecondaryCTA from './SecondaryCTA';
import { PaymentDetails } from './PaymentDetails';
import { DashboardSubheading } from '@common/components/DashboardSubheading/DashboardSubheading.component';
import TransactionConstants from '@common/constants/TransactionConstants';
import type { RenterPaymentMethodInterface, RenterPaymentSubscriptionInterface } from '@renter-hub/models/Types';
import type { MOVE_IN_COSTS_PAYMENT, RENT_PAYMENT } from '@renter-hub/constants/paymentConstants';
import { logError } from '@zg-rentals/log-error';

interface CurrentTransactionInterface {
  amount: number;
  zgFee?: number;
  totalAmount: number;
  renterStatus: string;
  dueDate: string;
  dateCreated?: string;
  dateReceived?: string;
}

interface NextPayableInterface {
  isScheduled: boolean;
}

/* REPLACE ABOVE WITH TYPES FROM SHARED TYPES FILE */

interface Props {
  currentTransaction?: CurrentTransactionInterface;
  nextPayable?: NextPayableInterface;
  paymentSubscriptionDetails?: RenterPaymentSubscriptionInterface;
  rentPaymentSubheading?: { statement: Array<any>; title: string };
  rentMonth?: string;
  landlordName?: string;
  accountType?: string;
  rentTermPaymentsComplete?: boolean;
  goToEditPaymentFrequencyPage?: () => any;
  goToEditPaymentSchedulingPage?: () => any;
  goToCancelPaymentPage?: () => any;
  displayHiddenText?: boolean;
  rentSubscriptionAutoCanceled?: boolean;
  paymentMethods?: Array<RenterPaymentMethodInterface>;
  goToEditPaymentInfoPage?: (typeOfPaymentToMake?: typeof MOVE_IN_COSTS_PAYMENT | typeof RENT_PAYMENT) => void;
}

const defaultProps: Props = {
  currentTransaction: {
    renterStatus: 'rejected',
    amount: 0,
    zgFee: 0,
    totalAmount: 0,
    dueDate: '',
  },
  nextPayable: { isScheduled: false },
  paymentSubscriptionDetails: {
    recurring: false,
    nextScheduledDate: '',
    nextArrivalDate: '',
  },
  rentPaymentSubheading: { statement: [], title: 'Next rent payment' },
  accountType: '',
  rentTermPaymentsComplete: false,
  goToEditPaymentFrequencyPage: () => {},
  goToEditPaymentSchedulingPage: () => {},
  goToCancelPaymentPage: () => {},
  rentSubscriptionAutoCanceled: false,
};

// getSubheadingTermComplete
const renderTermComplete = (props) => <DashboardSubheading {...props.rentPaymentSubheading} />;

// getSubheadingRentUnpaid
const renderUnpaid = (props) => {
  const { nextPayable, paymentSubscriptionDetails } = props;
  const { nextArrivalDate, recurring } = paymentSubscriptionDetails;

  /* scheduled */
  if (nextPayable.isScheduled && nextArrivalDate) {
    return (
      <>
        <DashboardSubheading {...props.rentPaymentSubheading} />
        <PaymentDetails {...props} />
        <SecondaryCTA onClick={props.goToCancelPaymentPage} recurring={recurring} />
      </>
    );
  }

  /* not scheduled */
  return (
    <>
      <DashboardSubheading {...props.rentPaymentSubheading} />
      <PrimaryCTA onClick={props.goToEditPaymentFrequencyPage} update={nextPayable.isScheduled} />
      <PaymentDetails {...props} />
    </>
  );
};

// getSubheadingPaymentPending
const renderPending = (props) => (
  <>
    <DashboardSubheading {...props.rentPaymentSubheading} />
    <PaymentDetails {...props} />
  </>
);

// getSubheadingPaymentComplete
const renderComplete = (props) => (
  <>
    <DashboardSubheading {...props.rentPaymentSubheading} />
    <Heading level={3} textStyle="heading-xs">
      <span role="img" aria-label="emoji-hands">
        🙌
      </span>
    </Heading>
    <PaymentDetails {...props} allowAutoPayEdit={props.rentSubscriptionAutoCanceled} />
  </>
);

// getSubheadingPaymentFailed
const renderFailed = (props) => {
  const { nextPayable } = props;

  return (
    <>
      <DashboardSubheading {...props.rentPaymentSubheading} />
      <PrimaryCTA onClick={props.goToEditPaymentFrequencyPage} update={nextPayable.isScheduled} />
      <PaymentDetails {...props} />
    </>
  );
};

// getSubheadingRentPastDue
const renderPastDue = (props) => {
  const { nextPayable, paymentSubscriptionDetails } = props;
  const { nextArrivalDate, recurring }: RenterPaymentSubscriptionInterface = paymentSubscriptionDetails;

  /* scheduled */
  if (nextArrivalDate) {
    return (
      <>
        <DashboardSubheading {...props.rentPaymentSubheading} />
        <PaymentDetails {...props} />
        <SecondaryCTA onClick={props.goToCancelPaymentPage} recurring={recurring} />
      </>
    );
  }

  /* not scheduled */
  return (
    <>
      <DashboardSubheading {...props.rentPaymentSubheading} />
      <PrimaryCTA onClick={props.goToEditPaymentFrequencyPage} update={nextPayable.isScheduled} />
    </>
  );
};

// getSubheadingManuallyPaid
const renderManual = (props) => {
  const markedByLandlord =
    getBooleanConfig('isLandlordMarkAsPaidOfflineEnabled', false) && Boolean(props.currentTransaction?.dateReceived);

  if (!markedByLandlord) {
    return <DashboardSubheading {...props.rentPaymentSubheading} />;
  }

  const { rentMonth, landlordName } = props;
  return (
    <>
      <Paragraph css={{ marginBottom: 'default' }}>
        {`Your ${rentMonth ? `${rentMonth} ` : ''}rent payment was `}
        <strong>marked as paid</strong>
        {' by your landlord. No action needed.'}
      </Paragraph>
      <PaymentDetails {...props} allowAutoPayEdit recordedByName={landlordName} />
    </>
  );
};

const renderError = (props) => {
  logError({
    error: new Error('Invalid transaction renterStatus'),
    context: {
      currentTransaction: props.currentTransaction,
    },
  });

  return <DashboardSubheading {...props.rentPaymentSubheading} />;
};

const renderDefault = (props) => {
  return <PaymentDetails {...props} />;
};

export const MonthlyRentCard: React.FC<React.PropsWithChildren<Props>> = (props) => {
  const { currentTransaction, rentPaymentSubheading, rentTermPaymentsComplete } = props;
  const { renterStatus } = currentTransaction!;

  let body;

  if (rentTermPaymentsComplete) {
    body = renderTermComplete(props);
  } else if (
    renterStatus === TransactionConstants.STATUS.UNPAID ||
    renterStatus === TransactionConstants.STATUS.REFUNDED
  ) {
    body = renderUnpaid(props);
  } else if (renterStatus === TransactionConstants.STATUS.PENDING) {
    body = renderPending(props);
  } else if (renterStatus === TransactionConstants.STATUS.COMPLETE) {
    body = renderComplete(props);
  } else if (
    /* past or future failed */
    renterStatus === TransactionConstants.STATUS.PAYMENT_FAILED ||
    renterStatus === TransactionConstants.STATUS.DEPOSIT_FAILED
  ) {
    body = renderFailed(props);
  } else if (renterStatus === TransactionConstants.STATUS.PAST_DUE) {
    body = renderPastDue(props);
  } else if (renterStatus === TransactionConstants.STATUS.MANUAL) {
    body = renderManual(props);
  } else if (props.displayHiddenText) {
    body = renderDefault(props);
  } else {
    body = renderError(props);
  }

  return (
    <Card css={{ padding: 'default', marginBottom: 'loose' }} elevated={false}>
      <Heading textStyle="heading-xs" level={3}>
        {rentPaymentSubheading!.title}
      </Heading>
      {body}
    </Card>
  );
};

MonthlyRentCard.defaultProps = defaultProps;
